/**
 * Export a Recharts chart (including its cell legend) as a PNG.
 *
 * The legend is a CSS div above the SVG, so we read each item's colour + label
 * from the DOM and draw them manually onto the canvas above the chart area.
 *
 * @param {HTMLElement} containerEl  The card div that wraps legend + chart
 * @param {string}      title        Chart title — used as the base filename
 * @param {string}      [dateFrom]   ISO date string for the view start
 * @param {string}      [dateTo]     ISO date string for the view end
 */
export function exportChartAsPng(containerEl, title, dateFrom, dateTo) {
  if (!containerEl) return;

  // ── Target the Recharts SVG specifically (not the icon button SVG) ───────────
  const svgEl = containerEl.querySelector('.recharts-wrapper svg')
             || containerEl.querySelector('svg:not([aria-hidden])');
  if (!svgEl) return;

  const SCALE   = 2;          // retina quality
  const MARGIN  = 14;         // horizontal padding for legend text
  const DOT_R   = 4;          // legend colour dot radius
  const ROW_H   = 22;         // height of each legend row
  const FONT_PX = 11;
  const FONT    = `${FONT_PX * SCALE}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;

  // ── Measure the chart SVG ────────────────────────────────────────────────────
  const bbox = svgEl.getBoundingClientRect();
  const svgW = bbox.width  || svgEl.viewBox?.baseVal?.width  || 800;
  const svgH = bbox.height || svgEl.viewBox?.baseVal?.height || 300;

  // ── Read legend items from DOM ───────────────────────────────────────────────
  const legendItems = [];
  const legendEl = containerEl.querySelector('.chart-legend');
  if (legendEl) {
    legendEl.querySelectorAll('.legend-item').forEach(item => {
      if (item.classList.contains('hidden')) return;
      const label = item.textContent.trim();
      if (!label || label === 'Show all') return;
      const dot = item.querySelector('.legend-dot');
      const color = dot ? dot.style.background : '#888';
      legendItems.push({ color, label });
    });
  }

  // ── Calculate legend layout (items flow left→right, wrap when needed) ────────
  // Use an off-screen canvas just for text measurement.
  const measurer = document.createElement('canvas').getContext('2d');
  measurer.font = FONT;

  const itemWidths = legendItems.map(({ label }) => {
    const textW = measurer.measureText(label).width;
    return textW / SCALE + DOT_R * 2 + 6 + MARGIN; // dot + gap + text + right-gap
  });

  let rows = [];
  let row = [];
  let rowW = MARGIN;
  itemWidths.forEach((w, i) => {
    if (rowW + w > svgW - MARGIN && row.length > 0) {
      rows.push(row);
      row  = [i];
      rowW = MARGIN + w;
    } else {
      row.push(i);
      rowW += w;
    }
  });
  if (row.length) rows.push(row);

  const legendH = rows.length > 0 ? rows.length * ROW_H + 8 : 0; // +8 bottom padding

  // ── Build SVG string for the chart area ──────────────────────────────────────
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('width',  svgW);
  clone.setAttribute('height', svgH);
  clone.setAttribute('xmlns',  'http://www.w3.org/2000/svg');
  const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bgRect.setAttribute('width', '100%'); bgRect.setAttribute('height', '100%');
  bgRect.setAttribute('fill', '#ffffff');
  clone.insertBefore(bgRect, clone.firstChild);
  const svgString = new XMLSerializer().serializeToString(clone);
  const svgBlob   = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl    = URL.createObjectURL(svgBlob);

  // ── Create combined canvas ────────────────────────────────────────────────────
  const totalH   = legendH + svgH;
  const canvas   = document.createElement('canvas');
  canvas.width   = svgW  * SCALE;
  canvas.height  = totalH * SCALE;
  const ctx = canvas.getContext('2d');

  // White background for the whole image
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // ── Draw legend rows ──────────────────────────────────────────────────────────
  if (legendItems.length > 0) {
    ctx.font = FONT;
    ctx.textBaseline = 'middle';

    rows.forEach((rowIndices, rowIdx) => {
      const rowY = (rowIdx * ROW_H + ROW_H / 2 + 4) * SCALE;
      let x = MARGIN * SCALE;

      rowIndices.forEach(i => {
        const { color, label } = legendItems[i];

        // Coloured dot
        ctx.beginPath();
        ctx.arc(x + DOT_R * SCALE, rowY, DOT_R * SCALE, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        // Label text
        ctx.fillStyle = '#3a4f6e';
        x += (DOT_R * 2 + 6) * SCALE;
        ctx.fillText(label, x, rowY);

        x += measurer.measureText(label).width + MARGIN * SCALE;
      });
    });
  }

  // ── Draw the chart SVG below the legend ───────────────────────────────────────
  const img = new Image();
  img.onload = () => {
    ctx.drawImage(img, 0, legendH * SCALE, svgW * SCALE, svgH * SCALE);
    URL.revokeObjectURL(svgUrl);

    // Build filename: "Title_2026-05-01_to_2026-05-28"
    const datePart = (dateFrom || dateTo)
      ? `_${(dateFrom || '').slice(0, 10)}${dateTo ? '_to_' + dateTo.slice(0, 10) : ''}`
      : '';
    const safe = (title + datePart).replace(/[/\\?%*:|"<>]/g, '_');

    const a = document.createElement('a');
    a.download = safe + '.png';
    a.href = canvas.toDataURL('image/png');
    a.click();
  };
  img.onerror = () => URL.revokeObjectURL(svgUrl);
  img.src = svgUrl;
}

/** Tiny download-icon SVG — no icon library needed. */
export function DownloadIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 1v9M5 7l3 3 3-3" stroke={color} strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12h12" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}
